import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { useCommonApi } from "src/_common/hooks/actions/common/appCommonApiHook";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";

interface CounterOffer {
  counter_offer_requested_salary?: number;
  counter_offer_currency_type?: string;
  counter_offer_preferred_start_date?: string;
  counter_offer_notes?: string;
  counter_offer_status?: number;
}

interface PendingAction extends CounterOffer {
  id: string;
  screening_id: string;
  job_id: string;
  jobseeker_id: number;
  candidate_name: string;
  candidate_email: string;
  job_title?: string;
  company_name?: string;
  offer_type: number;
  status: string;
  offer_letter_status?: number; // 1=accepted, 2=declined
  sent_at: string;
  expires_at: string;
  salary?: string;
  currency_type?: string;
}

interface PendingOfferActionsSectionProps {
  // Remove props since we'll fetch data internally
}

export default function PendingOfferActionsSection({}: PendingOfferActionsSectionProps) {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const jobListings = useJobListSelector();
  const userDetails = useAppUserDetailsSelector();
  const { callGetPendingActions } = useCommonApi();
  const { callGetEmployerJobs } = useEmployerJobApi();

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<string>("");
  const [searchName, setSearchName] = useState<string>("");
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    count: 0,
    has_more: false,
  });
  const itemsPerPage = 5;

  // Load jobs on component mount
  useEffect(() => {
    if (userDetails?.user?.company_id && userDetails?.user_type !== "member") {
      loadJobs();
    }
  }, [userDetails?.user?.company_id, userDetails?.user_type]);

  // Load pending actions on component mount
  useEffect(() => {
    loadPendingActions();
  }, []);

  // Reload data when page changes
  useEffect(() => {
    if (currentPage > 1) {
      loadPendingActions({
        job_id: selectedJob || undefined,
        name: searchName || undefined,
      });
    }
  }, [currentPage]);

  const loadJobs = () => {
    setLoadingJobs(true);
    const employerId = userDetails?.user?.company_id;

    if (!employerId) {
      console.error("Employer ID not found");
      setLoadingJobs(false);
      return;
    }

    callGetEmployerJobs(
      employerId,
      { job_status: ["ai_filter", "open", "rejected"] },
      (message: string, data: any) => {
        console.log("Jobs loaded successfully - data will be in Redux store");
        setLoadingJobs(false);
      },
      (error: string) => {
        console.error("Error loading jobs:", error);
        setLoadingJobs(false);
      },
    );
  };

  const loadPendingActions = (filters?: { job_id?: string; name?: string }) => {
    setLoading(true);
    const offset = (currentPage - 1) * itemsPerPage;
    const params: any = {
      limit: itemsPerPage,
      offset: offset,
    };

    if (filters?.job_id) {
      params.job_id = filters.job_id;
    }

    if (filters?.name) {
      params.name = filters.name;
    }

    callGetPendingActions(
      params,
      (message: string, data: any) => {
        console.log("Pending actions:", data);
        if (data && data.pending_actions) {
          setPendingActions(data.pending_actions);
        } else {
          setPendingActions([]);
        }

        // Update pagination info from API response
        if (data && data.pagination) {
          setPagination({
            total: data.pagination.total || 0,
            count: data.pagination.count || 0,
            has_more: data.pagination.has_more || false,
          });
        } else {
          setPagination({
            total: 0,
            count: 0,
            has_more: false,
          });
        }

        setLoading(false);
      },
      (error: string) => {
        console.error("Failed to load pending actions:", error);
        setPendingActions([]);
        setPagination({
          total: 0,
          count: 0,
          has_more: false,
        });
        setLoading(false);
      },
    );
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadPendingActions({
      job_id: selectedJob || undefined,
      name: searchName || undefined,
    });
  };

  const handleClearFilters = () => {
    setSelectedJob("");
    setSearchName("");
    setCurrentPage(1);
    loadPendingActions();
  };

  // Pagination calculations - now based on API pagination
  const totalPages = Math.ceil(pagination.total / itemsPerPage);
  const currentPageData = pendingActions; // Data is already paginated from API

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadge = (action: PendingAction) => {
    const offerLetterStatus = action.offer_letter_status; // 1=accepted, 2=declined
    const counterOfferStatus = action.counter_offer_status; // 1=pending, 2=approved, 3=rejected

    // Priority 1: Check if candidate accepted/declined the offer
    if (offerLetterStatus === 1) {
      // Offer Accepted (matches OfferStatus.tsx line 687)
      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: "#d1fae5",
            color: "#065f46",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          Offer Accepted
        </span>
      );
    } else if (offerLetterStatus === 2) {
      // Offer Declined (matches OfferStatus.tsx line 679)
      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          Offer Declined
        </span>
      );
    }

    // Priority 2: Check for counter offer status (matches OfferStatus.tsx exactly)
    if (counterOfferStatus === 1) {
      // Counter Offer Received (matches OfferStatus.tsx line 708)
      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: "#fef3c7",
            color: "#78350f",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          Counter Offer Received
        </span>
      );
    } else if (counterOfferStatus === 2) {
      // Counter Offer Approved (matches OfferStatus.tsx line 697)
      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: "#d1fae5",
            color: "#065f46",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          Counter Offer Approved
        </span>
      );
    } else if (counterOfferStatus === 3) {
      // Counter Offer Rejected (matches OfferStatus.tsx line 704)
      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          Counter Offer Rejected
        </span>
      );
    }

    // Priority 3: Check for offer sent status - Awaited (matches OfferStatus.tsx line 714)
    if (action.status === "sent" || action.status === "sent_file") {
      return (
        <span
          className="status-badge"
          style={{
            backgroundColor: "#dbeafe",
            color: "#1e40af",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: "600",
          }}
        >
          Awaiting Response
        </span>
      );
    }

    // Default: Offer Sent
    return (
      <span
        className="status-badge"
        style={{
          backgroundColor: "#e0e7ff",
          color: "#3730a3",
          padding: "6px 12px",
          borderRadius: "4px",
          fontSize: "13px",
          fontWeight: "600",
        }}
      >
        Offer Sent
      </span>
    );
  };

  const handleViewDetails = (action: PendingAction) => {
    // Navigate to job details page, final decision tab, with hired tab selected
    // The URL structure is: /job-details/:jobId
    // Query params to identify the screening: ?tab=final-decision&screening_id=xxx&status=hired
    history.push(`/job-details/${action.job_id}?tab=final-decision&screening_id=${action.screening_id}&status=hired`);
  };

  if (loading) {
    return (
      <div className="pending-offers-section" style={{ marginBottom: "24px" }}>
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            overflow: "hidden",
          }}
        >
          <div className="section-header" style={{ padding: "20px", borderBottom: "1px solid #e0e0e0" }}>
            <h2 className="section-title">{t("dashboard.pendingOfferActions.title")}</h2>
          </div>
          <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
            {t("dashboard.pendingOfferActions.loading")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-offers-section" style={{ marginBottom: "24px" }}>
      <div
        className="pending-offers-list"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        <div
          className="section-header"
          style={{ padding: "20px", borderBottom: "none", marginBottom: "0", background: "transparent" }}
        >
          <h2 className="section-title" style={{ marginBottom: "0" }}>
            {t("dashboard.pendingOfferActions.title")}
          </h2>
        </div>

        {/* Filter Controls */}
        <div
          className="filter-controls"
          style={{
            padding: "20px",
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            gap: "15px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Job Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1", minWidth: "200px", justifyContent: "flex-end" }}>
            <label htmlFor="job-filter" style={{ fontWeight: "600", whiteSpace: "nowrap", fontSize: "14px", minWidth: "50px" }}>
              {t("dashboard.pendingOfferActions.filters.job")}
            </label>
            <select
              id="job-filter"
              className="basic-select"
              style={{ flex: 1 }}
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
            >
              <option value="">{t("dashboard.pendingOfferActions.filters.allJobs")}</option>
              {jobListings && jobListings.length > 0 ? (
                jobListings.map((job: any) => (
                  <option key={job.emp_job_id} value={job.emp_job_id}>
                    {job.job_title}
                  </option>
                ))
              ) : (
                <option disabled>{t("dashboard.pendingOfferActions.filters.noJobs")}</option>
              )}
            </select>
          </div>

          {/* Name Search Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1", minWidth: "200px" }}>
            <label htmlFor="name-filter" style={{ fontWeight: "600", whiteSpace: "nowrap", fontSize: "14px", minWidth: "50px" }}>
              {t("dashboard.pendingOfferActions.filters.name")}
            </label>
            <input
              id="name-filter"
              type="text"
              className="form-control"
              placeholder={t("dashboard.pendingOfferActions.filters.namePlaceholder")}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              style={{ flex: 1 }}
            />
          </div>

          {/* Search Button */}
          <button
            className="find-btn"
            onClick={handleSearch}
            disabled={loading}
            style={{
              backgroundColor: "#1a1a1a",
              minHeight: "48px",
            }}
          >
            <i className="fa fa-search" style={{ marginRight: "5px" }}></i>
            {loading
              ? t("dashboard.pendingOfferActions.filters.searching")
              : t("dashboard.pendingOfferActions.filters.search")}
          </button>

          {/* Clear Filters Button */}
          {(selectedJob || searchName) && (
            <button
              onClick={handleClearFilters}
              disabled={loading}
              style={{
                padding: "8px 16px",
                backgroundColor: "#6c757d",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
                whiteSpace: "nowrap",
                opacity: loading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = "#5a6268";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#6c757d";
              }}
            >
              <i className="fa fa-times" style={{ marginRight: "5px" }}></i>
              {t("dashboard.pendingOfferActions.filters.clearFilters")}
            </button>
          )}
        </div>

        {currentPageData.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
            {loading
              ? t("dashboard.pendingOfferActions.loading")
              : selectedJob || searchName
                ? t("dashboard.pendingOfferActions.noResults")
                : t("dashboard.pendingOfferActions.noPendingActions")}
          </div>
        ) : (
          <>
            {currentPageData.map((action) => (
              <div
                key={action.id}
                className="pending-offer-item"
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "background-color 0.2s",
                  cursor: "pointer",
                }}
                onClick={() => handleViewDetails(action)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#e3f2fd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "600",
                      color: "#1976d2",
                      fontSize: "16px",
                    }}
                  >
                    {action.candidate_name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#333",
                      }}
                    >
                      {action.candidate_name}
                    </h3>
                    {action.job_title && (
                      <p
                        style={{
                          margin: "4px 0 0 0",
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        {action.job_title}
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {getStatusBadge(action)}
                  <i
                    className="fa fa-chevron-right"
                    style={{
                      color: "#999",
                      fontSize: "14px",
                    }}
                  ></i>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Pagination */}
      {currentPageData.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
            {t("dashboard.jobListings.pagination.prev")}
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`}>...</span>
              ) : (
                <button
                  key={page}
                  className={`page-num ${currentPage === page ? "active" : ""}`}
                  onClick={() => handlePageChange(page as number)}
                >
                  {page}
                </button>
              ),
            )}
          </div>
          <button className="page-btn" onClick={handleNextPage} disabled={currentPage === totalPages}>
            {t("dashboard.jobListings.pagination.next")}
          </button>
        </div>
      )}
    </div>
  );
}
